import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ExclamationTriangle, Bolt } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import EventConfigCard from './EventConfigCard';
import EventConfigEditor from './EventConfigEditor';
import { mergeEventDetectionChange } from '../../utils/eventMetricAvailability';
import { collectSpeedValues } from '../../utils/validationUtils';
import {
  EventSection,
  EventSectionHeader,
  EventSectionTitle,
  EnabledBadge,
  WarningBox,
  WarningIcon,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './EventDetection.styled';

const EventDetectionSection = ({
  sensor,
  eventDetection,
  savedEventDetection,
  editMode,
  onChange,
  eventMetadata,
  bulkMode,
  warningMessage,
  vibrationDirectionPreference,
  temperatureOnly,
  onValidationChange,
  getAlarmThresholds,
  getDashboardCouplingEligibility,
  user
}) => {
  const { t } = useTranslation();
  const configs = (eventDetection && eventDetection.event_configs) || [];
  // Absent means the backend did not say -- bulk mode has no per-mote payload --
  // so the magnetometer stays offered rather than silently disappearing.
  const magnetometerAvailable = !(
    eventDetection && eventDetection.magnetometer_available === false
  );
  // Scaled by the pole count behind this mote's location, so it is per mote and
  // cannot come from the globally cached metadata response.
  const speedLimits = (eventDetection && eventDetection.speed_limits) || null;
  const savedSpeedValues = useMemo(
    () => collectSpeedValues(savedEventDetection && savedEventDetection.event_configs),
    [savedEventDetection]
  );
  const [alarmThresholds, setAlarmThresholds] = useState(null);
  const [fetchingAlarms, setFetchingAlarms] = useState(false);
  const [dashboardCouplingEligibility, setDashboardCouplingEligibility] = useState(null);
  const [fetchingDashboardEligibility, setFetchingDashboardEligibility] = useState(false);

  // Check if sensor has measurement location
  const hasLocation = bulkMode || (sensor && sensor.location);
  const isEnabled = (eventDetection && eventDetection.enabled) || false;

  // Function to fetch alarm thresholds
  const fetchAlarmThresholds = useCallback(() => {
    if (sensor && sensor.location && getAlarmThresholds) {
      setFetchingAlarms(true);
      const unitSystem = (user && user.unit_system) || 1; // Default to metric (1) if not available
      return getAlarmThresholds(sensor.location, unitSystem)
        .then((data) => {
          // eslint-disable-next-line no-console
          console.log('[EventDetection] Fetched alarm thresholds:', data);
          setAlarmThresholds(data);
          setFetchingAlarms(false);
          return data;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch alarm thresholds:', error);
          setFetchingAlarms(false);
          // Set empty alarm thresholds to show missing state
          const emptyThresholds = {
            temperature: null,
            vibration: {},
            available_bins: [],
            missing: { temperature: true, vibration: {} }
          };
          setAlarmThresholds(emptyThresholds);
          return emptyThresholds;
        });
    }
    return Promise.resolve(null);
  }, [sensor, getAlarmThresholds, user]);

  const fetchDashboardEligibility = useCallback(() => {
    if (sensor && sensor.location && getDashboardCouplingEligibility) {
      setFetchingDashboardEligibility(true);
      return getDashboardCouplingEligibility(sensor.location)
        .then((data) => {
          setDashboardCouplingEligibility(data);
          setFetchingDashboardEligibility(false);
          return data;
        })
        .catch(() => {
          setDashboardCouplingEligibility({ eligible: false, reason: 'error' });
          setFetchingDashboardEligibility(false);
          return { eligible: false };
        });
    }
    return Promise.resolve(null);
  }, [sensor, getDashboardCouplingEligibility]);

  useEffect(() => {
    setAlarmThresholds(null);
    setDashboardCouplingEligibility(null);
  }, [sensor && sensor.location]);

  useEffect(() => {
    if (editMode && hasLocation && sensor && sensor.location) {
      if (getAlarmThresholds && alarmThresholds === null) {
        fetchAlarmThresholds();
      }
      if (getDashboardCouplingEligibility && dashboardCouplingEligibility === null) {
        fetchDashboardEligibility();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, hasLocation, sensor && sensor.location, alarmThresholds, dashboardCouplingEligibility]);

  // Filter configs to only show temperature-based configs when temperatureOnly is true (safety check)
  const filteredConfigs = useMemo(() =>
    temperatureOnly
      ? configs.filter((config) => {
        if (!config || !config.conditions) return false;
        return config.conditions.every(condition => condition.metric === 'Temperature');
      })
      : configs,
  [temperatureOnly, configs]
  );

  const handleChange = useCallback((newConfigs, enabled) => {
    onChange(mergeEventDetectionChange(
      eventDetection, newConfigs, enabled, sensor && sensor.location
    ));
  }, [onChange, sensor, eventDetection]);

  const renderHeader = useCallback(() => (
    <>
      <EventSectionHeader>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EventSectionTitle>{t('sensors.eventDetection.title')}</EventSectionTitle>
          <EnabledBadge enabled={isEnabled}>
            {isEnabled
              ? t('sensors.eventDetection.enabled')
              : t('sensors.eventDetection.disabled')}
          </EnabledBadge>
        </div>
      </EventSectionHeader>
      {warningMessage && (
        <WarningBox>
          <WarningIcon>
            <ExclamationTriangle aria-label={t('common.aria.warning', { defaultValue: 'warning' })} />
          </WarningIcon>
          <span>{warningMessage}</span>
        </WarningBox>
      )}
      {temperatureOnly && (
        <WarningBox>
          <WarningIcon>
            <ExclamationTriangle aria-label={t('common.aria.info', { defaultValue: 'info' })} />
          </WarningIcon>
          <span>
            {t('sensors.eventDetection.temperatureOnlyWarning')}
          </span>
        </WarningBox>
      )}
    </>
  ), [isEnabled, warningMessage, temperatureOnly, t]);

  // When in edit mode and sensor has location, show editor
  if (editMode && hasLocation) {
    return (
      <EventSection>
        {renderHeader()}
        <EventConfigEditor
          initialConfigs={filteredConfigs}
          initialEnabled={isEnabled}
          onChange={handleChange}
          eventMetadata={eventMetadata}
          hideTitle
          vibrationDirectionPreference={vibrationDirectionPreference}
          temperatureOnly={temperatureOnly}
          magnetometerAvailable={magnetometerAvailable}
          speedLimits={speedLimits}
          savedSpeedValues={savedSpeedValues}
          onValidationChange={onValidationChange}
          alarmThresholds={alarmThresholds}
          fetchingAlarms={fetchingAlarms}
          dashboardCouplingEligibility={dashboardCouplingEligibility}
          fetchingDashboardEligibility={fetchingDashboardEligibility}
          getDashboardCouplingEligibility={getDashboardCouplingEligibility}
          user={user}
        />
      </EventSection>
    );
  }

  return (
    <EventSection>
      {renderHeader()}

      {!hasLocation && (
        <WarningBox>
          <WarningIcon>
            <ExclamationTriangle aria-label={t('common.aria.warning', { defaultValue: 'warning' })} />
          </WarningIcon>
          <span>
            {t('sensors.eventDetection.requiresLocation')}
          </span>
        </WarningBox>
      )}

      {isEnabled && filteredConfigs.length > 0 && (
        <div>
          {filteredConfigs.map((config, index) => (
            <EventConfigCard
              key={index}
              config={config}
              vibrationDirectionPreference={vibrationDirectionPreference}
              eventMetadata={eventMetadata}
            />
          ))}
        </div>
      )}

      {isEnabled && filteredConfigs.length === 0 && (
        <EmptyState>
          <EmptyStateIcon>
            <Bolt aria-label={t('common.aria.lightning', { defaultValue: 'lightning' })} />
          </EmptyStateIcon>
          <EmptyStateText>
            {t('sensors.eventDetection.emptyNoRules')}
          </EmptyStateText>
        </EmptyState>
      )}
    </EventSection>
  );
};

EventDetectionSection.propTypes = {
  sensor: PropTypes.object,
  eventDetection: PropTypes.shape({
    event_configs: PropTypes.array,
    enabled: PropTypes.bool,
    measurement_location_id: PropTypes.number
  }),
  // The server's copy, for the speed band check; eventDetection holds the edits.
  savedEventDetection: PropTypes.shape({
    event_configs: PropTypes.array
  }),
  editMode: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  eventMetadata: PropTypes.object,
  bulkMode: PropTypes.bool,
  warningMessage: PropTypes.string,
  vibrationDirectionPreference: PropTypes.number,
  temperatureOnly: PropTypes.bool,
  onValidationChange: PropTypes.func,
  getAlarmThresholds: PropTypes.func,
  getDashboardCouplingEligibility: PropTypes.func,
  user: PropTypes.object
};

EventDetectionSection.defaultProps = {
  bulkMode: false,
  eventMetadata: null,
  sensor: null,
  eventDetection: null,
  savedEventDetection: null,
  warningMessage: null,
  vibrationDirectionPreference: 1,
  temperatureOnly: false,
  onValidationChange: null,
  getAlarmThresholds: null,
  getDashboardCouplingEligibility: null,
  user: null
};

export default EventDetectionSection;
